import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CalendarDays, ChevronDown, Pencil } from "lucide-react";
import { fmtDate, todayIso } from "./programUtils";

// Business milestones. Authoritative source: the pipeline stage history (corrected business date when recorded,
// otherwise the date the stage was entered). Other stored copies are only compared, never used to overwrite.
const MILESTONES = [
  { key: "lead", label: "Original Lead Date", match: (n) => n === "lead" || n.startsWith("lead "), person: "lead_date" },
  { key: "first_interview", label: "First Interview", match: (n) => n.includes("first interview"), person: "first_interview_date" },
  { key: "approved", label: "Approval", match: (n) => n.includes("approv"), person: "approved_date" },
  { key: "reserve", label: "Reserve", match: (n) => n.includes("reserve"), person: "reserve_date", enr: "actual_reserve_date" },
  { key: "target_career", label: "Target Career", target: true },
  { key: "career", label: "Career", match: (n) => n === "career" || n.startsWith("career "), person: "career_date", enr: "career_date" },
  { key: "fta", label: "FTA", match: (n) => n === "fta" || n.includes("fta"), person: "fta_date", enr: "fta_date" },
];
const d10 = (v) => (v ? String(v).slice(0, 10) : null);

function resolve(m, hist, person, enr, opp, passed) {
  if (m.target) {
    const value = d10(enr?.projected_appointment_date);
    const cfv = d10(person?.custom_fields?.target_career_date);
    return { value, source: "Target", target: true, idx: null, conflicts: cfv && cfv !== value ? [`${fmtDate(cfv)} in the Target Career Date custom field`] : [] };
  }
  const i = hist.findIndex((h) => m.match((h.stage_name || "").toLowerCase().trim()));
  const h = i > -1 ? hist[i] : null;
  let value = h ? d10(h.effective_date || h.entered_at) : null;
  let source = h ? (h.effective_date_auto ? "Oldest recorded date (auto-reconciled)" : h.effective_date_by === "Confirmed date exception" ? "Confirmed date exception" : h.effective_date ? `Corrected by ${h.effective_date_by || "staff"}` : "Stage entered (system date)") : null;
  if (!value && m.key === "lead" && opp?.open_date) { value = d10(opp.open_date); source = "Opportunity opened"; }
  const raw = [[m.person && person?.[m.person], "saved on the person"], [m.enr && enr?.[m.enr], "saved on the program enrollment"]]
    .filter(([v]) => v && d10(v) !== value);
  if (!value && raw.length && new Set(raw.map(([v]) => d10(v))).size === 1) {
    return { value: d10(raw[0][0]), source: `Recorded date (${raw[0][1].replace("saved on the ", "")})`, idx: h ? i : null, conflicts: [] };
  }
  const others = raw.map(([v, where]) => `${fmtDate(v)} ${where}`);
  if (!value && others.length) source = "No stage history";
  if (!value && !others.length && passed?.(m)) source = "Stage passed — date not recorded";
  return { value, source, idx: h ? i : null, conflicts: others };
}

function DateRow({ m, r, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(r.value || "");
  const future = r.value && r.value > todayIso();
  const editable = canEdit && (r.idx !== null || m.target);
  return (
    <div className="py-2 border-b border-slate-50 last:border-0" data-testid={`key-date-${m.key}`}>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-slate-500">{m.label}</span>
        {editing ? (
          <span className="flex items-center gap-1.5">
            <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" data-testid={`key-date-input-${m.key}`} />
            <button onClick={async () => { if (await onSave(m, r, draft)) setEditing(false); }} className="text-emerald-700 text-xs font-semibold" data-testid={`key-date-save-${m.key}`}>Save</button>
            <button onClick={() => setEditing(false)} className="text-slate-400 text-xs">Cancel</button>
          </span>
        ) : (
          <span className="flex items-center gap-2">
            {r.value ? <span className="text-slate-900 tabular-nums font-medium">{fmtDate(r.value)}</span> : <span className="text-slate-400 text-xs">Not recorded</span>}
            {r.target && r.value && <span className="text-[10px] rounded-full bg-sky-50 text-sky-700 px-1.5 py-0.5">Target</span>}
            {!r.target && future && <span className="text-[10px] rounded-full bg-sky-50 text-sky-700 px-1.5 py-0.5">Projected</span>}
            {editable && <button onClick={() => { setDraft(r.value || ""); setEditing(true); }} className="text-slate-400 hover:text-brand" aria-label={`Edit ${m.label}`} data-testid={`key-date-edit-${m.key}`}><Pencil className="w-3.5 h-3.5" /></button>}
          </span>
        )}
      </div>
      {r.source && !r.target && <p className={`text-[11px] text-right ${r.value ? "text-slate-400" : "text-amber-700"}`}>{r.source}</p>}
      {r.conflicts.length > 0 && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1 justify-end text-right" data-testid={`key-date-conflict-${m.key}`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />Needs review: also {r.conflicts.join("; ")}
        </p>
      )}
    </div>
  );
}

export function KeyDatesCard({ person, opp, enrollment, canEdit, onReload }) {
  const [more, setMore] = useState(false);
  const hist = opp?.stage_history || [];
  const stages = [...(opp?.pipeline?.stages || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const cur = stages.findIndex((s) => s.id === opp?.current_stage_id);
  const passed = (m) => m.match && stages.some((s, i) => i <= cur && m.match((s.name || "").toLowerCase().trim()));
  const rows = MILESTONES.map((m) => ({ m, r: resolve(m, hist, person, enrollment, opp, passed) }))
    .filter(({ m, r }) => r.value || r.conflicts.length || m.key === "lead" || (m.target && enrollment) || (m.key === "reserve" && enrollment) || passed(m));
  const hasConflict = rows.some(({ m, r }) => !m.target && r.conflicts.length);
  const tried = useRef(false);
  useEffect(() => {
    if (!hasConflict || tried.current || !person?.id) { return; }
    tried.current = true;
    api.post(`/date-review/auto/${person.id}`).then(({ data }) => { if (data.resolved) onReload?.(); }).catch(() => {});
  }, [hasConflict, person?.id, onReload]);
  const save = async (m, r, draft) => {
    try {
      if (m.target) await api.patch(`/persons/${person.id}/important-dates`, { dates: { target_career_date: draft || null } });
      else await api.patch(`/opportunities/${opp.id}/stage-history/${r.idx}`, { date: draft || null });
      toast.success(`${m.label} updated`); onReload?.(); return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="key-dates">
      <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2 mb-1"><CalendarDays className="w-4 h-4 text-brand" />Important Dates &amp; Key Milestones</h3>
      <div>{rows.map(({ m, r }) => <DateRow key={m.key} m={m} r={r} canEdit={canEdit} onSave={save} />)}</div>
      {hist.length > 0 && (
        <>
          <button onClick={() => setMore((v) => !v)} className="mt-2 text-xs font-medium text-brand inline-flex items-center gap-1" aria-expanded={more} data-testid="key-dates-more">
            All stage dates <ChevronDown className={`w-3.5 h-3.5 transition-transform ${more ? "rotate-180" : ""}`} />
          </button>
          {more && (
            <ul className="mt-2 space-y-1 text-xs" data-testid="key-dates-history">
              {hist.map((h, i) => (
                <li key={i} className="flex justify-between gap-2 text-slate-600">
                  <span>{h.stage_name}</span>
                  <span className="tabular-nums">{fmtDate(h.effective_date || h.entered_at)}{h.effective_date ? <span className="text-slate-400"> (system {fmtDate(h.entered_at)})</span> : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
